import { useEffect } from 'react'
import { useParams } from 'react-router'
import { GapFinder } from '@/components/GapFinder'
import { LiveSearchBadge } from '@/components/LiveSearchBadge'
import { Overview } from '@/components/Overview'
import { PanelSwitch } from '@/components/PanelSwitch'
import { QueryAccordion } from '@/components/QueryAccordion'
import { RunProgress } from '@/components/RunProgress'
import { SegmentedIndicator } from '@/components/SegmentedIndicator'
import { modeLabel } from '@/lib/format'
import { formatPerformanceTimestamp } from '@/lib/performanceFormat'
import { useInvalidatePerformanceOnCompletion } from '@/lib/performanceQueries'
import { useRun } from '@/lib/queries'
import { useTabDirection } from '@/lib/useTabDirection'
import { useAnalytics } from '@/provider/useAuth'

const TAB_INDICATOR = 'run-tab-underline'

const TABS = ['overview', 'explorer'] as const

const TAB_LABEL: Record<(typeof TABS)[number], string> = {
  overview: 'Overview',
  explorer: 'Query Explorer',
}

export default function RunDashboard() {
  const { runId } = useParams<{ runId: string }>()
  const { data: run, isPending, isError } = useRun(runId ?? '')
  // Immediately after useRun, before any early return, so hook order never changes.
  useInvalidatePerformanceOnCompletion(run?.id, run?.status)
  const tabs = useTabDirection(TABS, 'overview')
  const analytics = useAnalytics()

  useEffect(() => {
    if (run?.status === 'complete')
      analytics.track('dashboard_viewed', { runId: run.id })
  }, [run?.status, run?.id, analytics])

  if (isPending)
    return (
      <div className="p-8 text-center text-muted-foreground">Loading run…</div>
    )
  if (isError || !run)
    return (
      <div className="p-8 text-center text-destructive">
        Could not load this run.
      </div>
    )

  const live = run.status === 'pending' || run.status === 'running'

  return (
    <main className="px-6 pb-20 mx-auto w-full max-w-[2000px] 2xl:px-12">
      <section className="pt-8 pb-4">
        <h1 className="text-3xl font-semibold">
          {run.brand}{' '}
          <span className="text-muted-foreground font-normal">in</span>{' '}
          <span className="capitalize">{run.category}</span>
        </h1>
        <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
          <span>
            {run.targetMarketLabel ?? run.market ?? 'Global'} ·{' '}
            {modeLabel(run.mode)} ·{' '}
            <time dateTime={run.measurementAt}>
              {formatPerformanceTimestamp(run.measurementAt)}
            </time>
          </span>
          {run.liveSearch && <LiveSearchBadge />}
        </div>
      </section>

      {live && (
        <RunProgress
          phase={run.phase}
          cellsDone={run.cellsDone}
          cellsTotal={run.cellsTotal}
        />
      )}

      {run.status === 'error' && (
        <div className="mt-12 max-w-lg mx-auto text-center">
          <p className="text-destructive mb-3">This run failed to complete.</p>
          <p className="text-sm text-muted-foreground">
            Start a new run from the home page.
          </p>
        </div>
      )}

      {run.status === 'complete' && run.mode === 'intent_map' && (
        <div className="mt-8">
          <GapFinder run={run} />
        </div>
      )}

      {run.status === 'complete' && run.mode !== 'intent_map' && (
        <>
          <div className="mt-6 flex gap-1 border-b border-border">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  if (t === tabs.active) return

                  tabs.select(t)
                  analytics.track('tab_switched', { tab: t })
                }}
                className={`relative cursor-pointer px-4 py-2.5 text-sm font-medium -mb-px transition-colors ${tabs.active === t ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {tabs.active === t && (
                  <SegmentedIndicator
                    layoutId={TAB_INDICATOR}
                    className="absolute inset-x-0 bottom-0 h-0.5 bg-primary"
                  />
                )}
                {TAB_LABEL[t]}
              </button>
            ))}
          </div>
          <div className="mt-8">
            <PanelSwitch
              panelKey={tabs.active}
              index={tabs.index}
              previousIndex={tabs.previousIndex}
            >
              {tabs.active === 'overview' ? (
                <Overview run={run} />
              ) : (
                <QueryAccordion run={run} />
              )}
            </PanelSwitch>
          </div>
        </>
      )}
    </main>
  )
}
